import type {
  CurrencyCode,
  CustomTxInput,
  GetExternalAccountSummaryQuery,
} from 'hooks/customerApi';
import { useToast } from 'components/ToastManager';
import isValidDate from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import { CurrencyMode, useSyncCustomTxMutation } from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { getResultOrError } from 'utils/errors';

import { SchemaCurrencyMatchInput } from '@fragment/schema-validation/types';
import { toBigInt } from '@fragment/shared-utils/bigints';
import { SafeStringSchema } from '@fragment/types';
import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import { transformInputToCurrency } from '../SchemasV2/EditEntry/utils';

type ExternalAccount = NonNullable<
  NonNullable<GetExternalAccountSummaryQuery>['externalAccount']
>;

type SyncCustomTxModalProps = {
  externalAccount: ExternalAccount;
  onClose: VoidFunction;
  linkId: string;
  refresh: VoidFunction;
};

type FormInput = Omit<CustomTxInput, 'currency'> & {
  currency: string;
};

export const SyncCustomTxModal = ({
  externalAccount,
  onClose,
  linkId,
  refresh,
}: SyncCustomTxModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty, errors: activeError },
  } = useForm<FormInput>({
    mode: 'onTouched',
    defaultValues: {
      account: { id: externalAccount.id },
      externalId: '',
      posted: new Date().toISOString(),
      description: '',
      amount: '',
    },
  });

  const { context } = useCustomerApiContext();
  const [, syncCustomTxMutation] = useSyncCustomTxMutation();
  const { showToast } = useToast();
  const createTx = useCallback(
    async (tx: FormInput) => {
      let currency;
      if (externalAccount.currencyMode === CurrencyMode.Multi) {
        const currencyInput = transformInputToCurrency(tx.currency);
        currency = SchemaCurrencyMatchInput.safeParse(currencyInput);
        if (!currency.success) {
          // This should never happen, but we'll show an error if it does
          showToast(<Toast type="error" message="Invalid currency" />);
          return;
        }
        currency = {
          code: currency.data.code as CurrencyCode,
          customCurrencyId: currency.data.customCurrencyId,
        };
      }
      const { data: syncCustomTxData, error: syncCustomTxError } =
        await syncCustomTxMutation(
          {
            tx: {
              ...tx,
              currency,
            },
            link: { id: linkId },
          },
          context
        );
      const syncCustomTxRes = getResultOrError(
        syncCustomTxData?.syncCustomTxs,
        syncCustomTxError
      );
      if (syncCustomTxRes.isError) {
        showToast(
          <Toast
            type="error"
            message={`Failed to add Tx: ${syncCustomTxRes.errorMessage}`}
          />
        );
        return;
      }

      showToast(<Toast type="success" message="Successfully added Tx" />);
      refresh();
      onClose();
    },
    [
      context,
      linkId,
      syncCustomTxMutation,
      showToast,
      refresh,
      onClose,
      externalAccount.currencyMode,
    ]
  );

  return (
    <Modal
      onClose={onClose}
      header={<span>Add Tx</span>}
      width="sm"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
      onSubmit={handleSubmit(createTx)}
      body={
        <div className="flex flex-col gap-y-[1px]">
          <div className="flex flex-col gap-y-[1px]">
            <ModalField label="External ID" hasError={!!activeError.externalId}>
              <ModalTextInput
                {...register('externalId', {
                  required: 'External ID is required',
                  validate: (value) => {
                    const safeString = SafeStringSchema.safeParse(value);
                    if (!safeString.success) {
                      return 'Invalid external ID';
                    }
                    return true;
                  },
                })}
                placeholder="tx-external-id"
                data-testid="external-id-input"
                hasError={!!activeError.externalId}
              />
            </ModalField>

            <ModalField
              label="Description"
              hasError={!!activeError.description}
            >
              <ModalTextInput
                {...register('description', {
                  required: 'Description is required',
                })}
                placeholder="Transaction Description"
                data-testid="description-input"
                hasError={!!activeError.description}
              />
            </ModalField>

            <ModalField label="Amount" hasError={!!activeError.amount}>
              <ModalTextInput
                {...register('amount', {
                  required: 'Amount is required',
                  validate: (value) => {
                    try {
                      toBigInt(value);
                      return true;
                    } catch (e) {
                      return 'Invalid amount';
                    }
                  },
                })}
                placeholder="10000"
                data-testid="amount-input"
              />
            </ModalField>
            {externalAccount.currencyMode === CurrencyMode.Multi && (
              <ModalField label="Currency" hasError={!!activeError.currency}>
                <ModalTextInput
                  {...register('currency', {
                    validate: (value) => {
                      const currency = transformInputToCurrency(value);
                      const currencyMatchInput =
                        SchemaCurrencyMatchInput.safeParse(currency);
                      if (!currencyMatchInput.success) {
                        return 'Invalid currency';
                      }
                      return true;
                    },
                  })}
                  placeholder="ex: USD"
                  data-testid="currency-input"
                />
              </ModalField>
            )}

            <ModalField label="Posted Date" hasError={!!activeError.posted}>
              <ModalTextInput
                {...register('posted', {
                  required: 'Posted date is required',
                  validate: (value) => {
                    const date = parseISO(value);
                    if (isValidDate(date)) {
                      return true;
                    }
                    return 'Invalid date';
                  },
                })}
                placeholder="YYYY-MM-DD"
                data-testid="posted-input"
              />
            </ModalField>
          </div>
        </div>
      }
      footer={
        <FormButtons
          type="secondary"
          cancelButtonLabel="Cancel"
          onCancel={onClose}
          submitButtonLabel="Add"
          disabled={isSubmitting || !isValid || !isDirty}
        />
      }
    />
  );
};
